// Posting — the breakdown, the network rule and the reference written to the PMS
// (H1 §1–§4, D196–D199). Pure helpers over a posting item; the only outside read
// is the shared Networks store (one source for "who is in network with whom").
import { isOutOfNetwork } from '@/features/networks/store';
import type { BreakdownRow, LineItem, PaymentRef, PostingItem } from './types';
import { money, PMS } from './utils';
import { CARD_FEE_RATE } from './cardFee';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** "Dr. Marcus Lee" → "Dr. Lee". */
export function dentistShort(dentist: string): string {
  const parts = dentist.trim().split(/\s+/);
  const last = parts[parts.length - 1] ?? dentist;
  return `${parts[0] ?? 'Dr.'} ${last}`;
}

/** Out of network with this payer (Settings › Practice details › Networks). Payer by its short name. */
export function isOon(x: { dentist: string; payer: { short: string } }): boolean {
  return isOutOfNetwork(x.dentist, x.payer.short);
}

/** The words after "Reference written to Dentrix · " (D199). */
export function referenceText(pay: PaymentRef, payerShort: string, fallbackTrace: string): string {
  switch (pay.method) {
    case 'check':
      return `${payerShort} check ${pay.checkNo ?? ''}`.trim();
    case 'card':
      return `Card ··${pay.last4 ?? ''} · approval ${pay.approval ?? ''}`;
    case 'eob':
      return `${payerShort} EOB ${pay.eobDate ?? ''}`.trim();
    case 'eft':
    default:
      return `EFT trace ${pay.trace ?? fallbackTrace}`;
  }
}

export function referenceLine(pay: PaymentRef, payerShort: string, fallbackTrace: string): string {
  return `Reference written to ${PMS} · ${referenceText(pay, payerShort, fallbackTrace)}`;
}

/**
 * The card fee line, only when the payer paid by card (D195, D283). It is its own line: the insurance
 * payment above it stays at its FULL amount, and the fee never reduces the claim's paid amount.
 */
export function cardFeeLine(pay: PaymentRef, paid: number): string | null {
  if (pay.method !== 'card') return null;
  return `Card fee · 3% · ${money(round2(paid * CARD_FEE_RATE))} · a processing cost, not on the patient’s account`;
}

/** A posted line under the network rule: out of network has no contractual write-off. */
export function lineForNetwork(l: LineItem, oon: boolean): LineItem {
  if (!oon) return l;
  return { ...l, writeOff: 0, owes: round2(l.fee - l.paid) };
}

export function noWriteOffLabel(dentist: string, payerShort: string): string {
  return `No write-off · ${dentistShort(dentist)} is out of network with ${payerShort}`;
}

/**
 * The rows of "What ELVA will post". Always two steps (Design-system additions §2):
 *   paid to the practice + write-off + the patient's share = the fee;
 *   the patient's share − what the patient already paid = still owed (negative: a patient credit).
 * Totals are computed from the lines, never typed.
 */
export function itemBreakdown(item: PostingItem): BreakdownRow[] {
  const oon = isOon(item);
  const special = item.kind === 'paid_to_patient' || item.kind === 'patient_credit';
  const stored = item.breakdown ?? [];

  if (special && item.fee != null) {
    const fee = item.fee;
    const paid = item.amount;
    const share = round2(fee - paid);
    const visit = item.visitPaid ?? 0;
    const rows: BreakdownRow[] = [
      { label: `${item.payer.short} paid you`, amount: paid },
      { label: noWriteOffLabel(item.dentist, item.payer.short), amount: 0, soft: true },
      { label: `${item.firstName}’s share`, amount: share },
      { label: 'Fee', amount: fee, kind: 'total' },
    ];
    if (visit > 0) {
      rows.push({ label: `${item.firstName} paid at the visit`, amount: -visit, kind: 'neg' });
      const left = round2(share - visit);
      if (left < 0) {
        rows.push({
          label: `Left on ${item.firstName}’s account as credit`,
          amount: -left,
          kind: 'total',
          badge: { label: 'Patient credit', tone: 'info' },
        });
      } else {
        rows.push({ label: `${item.firstName} still owes`, amount: left, kind: 'total' });
      }
    }
    return rows;
  }

  if (!oon) return stored;

  // Out of network, a normal payment: no write-off, the patient owes fee − paid.
  if (item.group === 'ready' && item.lines.length > 0) {
    const fee = round2(item.lines.reduce((s, l) => s + l.fee, 0));
    const paid = round2(item.lines.reduce((s, l) => s + l.paid, 0));
    return [
      { label: `${item.payer.short} paid you`, amount: paid },
      { label: noWriteOffLabel(item.dentist, item.payer.short), amount: 0, soft: true },
      { label: `${item.firstName}’s share`, amount: round2(fee - paid) },
      { label: 'Fee', amount: fee, kind: 'total' },
    ];
  }

  // Out of network, a hand-built breakdown (Needs you): swap the write-off for the no-write-off line.
  const wo = stored.find((r) => r.label.startsWith('Write-off'));
  const lost = wo?.amount ?? 0;
  return stored.map((r) => {
    if (r.label.startsWith('Write-off')) return { label: noWriteOffLabel(item.dentist, item.payer.short), amount: 0, soft: true };
    if (r.label.endsWith(' owes') && r.kind !== 'neg') return { ...r, amount: round2(r.amount + lost) };
    return r;
  });
}

/** The sum check for a breakdown (used by the sanity script and dev assertions). */
export function breakdownAddsUp(rows: BreakdownRow[]): boolean {
  const fee = rows.find((r) => r.label === 'Fee' && r.kind === 'total');
  if (!fee) return true;
  const idx = rows.indexOf(fee);
  const sum = round2(rows.slice(0, idx).reduce((s, r) => s + r.amount, 0));
  return sum === fee.amount;
}
